import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { RequestForm } from "@/components/site/RequestForm";
import { useT } from "@/lib/i18n";
import { fmtPrice, mediaUrl } from "@/lib/api";

const AV_CLS = { in_stock: "text-[#E6D5AC] border-[#C8AE6E]/50", reserved: "text-[#A39E93] border-[#A39E93]/40", on_order: "text-[#F3EFE6]/80 border-[#F3EFE6]/25" };

export function AvailBadge({ value }) {
  const { t } = useT();
  return <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${AV_CLS[value]}`} data-testid="availability-badge">
    <span className={`w-1.5 h-1.5 rounded-full ${value === "in_stock" ? "bg-[#C8AE6E]" : value === "reserved" ? "bg-[#A39E93]" : "bg-[#F3EFE6]/40"}`} />{t.avail[value]}
  </span>;
}

export function Price({ w, className = "text-xl" }) {
  const { t, lang } = useT();
  if (w.price_on_request || !w.price_cents) return <span className={`price ${className}`} data-testid="watch-price">{t.on_request}</span>;
  if (w.promo_price_cents) return (
    <span className="flex items-baseline gap-3" data-testid="watch-price">
      <span className={`price ${className}`}>{fmtPrice(w.promo_price_cents, lang)}</span>
      <s className="text-sm text-[#A39E93]">{fmtPrice(w.price_cents, lang)}</s>
    </span>
  );
  return <span className={`price ${className}`} data-testid="watch-price">{fmtPrice(w.price_cents, lang)}</span>;
}

export function WatchImage({ image, alt, size = "md", className = "" }) {
  const { lang } = useT();
  return (
    <div className={`photo-frame grain aspect-[4/5] ${className}`}>
      {image ? <img src={mediaUrl(image[size] || image.md)} alt={image[`alt_${lang}`] || alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]" />
        : <img src="/emblem-square.png" alt="" className="absolute inset-0 m-auto w-1/3 opacity-30" />}
    </div>
  );
}

export const formTypeFor = (av) => (av === "in_stock" ? "purchase" : av === "on_order" ? "order" : "waitlist");

export function QuickRequest({ w, open, onOpenChange, type, source = "catalogo" }) {
  const { t } = useT();
  const ft = type || formTypeFor(w.availability);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161512] border-[#C8AE6E]/25 text-[#F3EFE6] max-w-lg max-h-[92vh] overflow-y-auto" data-testid={`quick-request-dialog-${w.slug}`}>
        <DialogTitle className="sr-only">{w.brand} {w.model}</DialogTitle>
        <div className="flex items-center gap-4 mb-2">
          <div className="w-16 shrink-0"><WatchImage image={w.image} alt={w.model} size="sm" /></div>
          <div><p className="eyebrow">{w.brand}</p><p className="font-display text-xl">{w.model}</p><Price w={w} className="text-base" /></div>
        </div>
        <p className="eyebrow mb-2">{ft === "order" ? t.order_wa : ft === "waitlist" ? t.waitlist : t.buy_wa}</p>
        <RequestForm key={`${w.id}-${ft}`} type={ft} source={source} compact prefill={{ watch_id: w.id, watch_label: `${w.brand} ${w.model}`, message: "" }} testid={`quick-${w.slug}-form`} />
      </DialogContent>
    </Dialog>
  );
}

export function WatchCard({ w, delay = 0 }) {
  const { t, r } = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="reveal" style={{ animationDelay: `${delay}ms` }}>
    <Link to={`${r.watch}/${w.slug}`} className="group block" data-testid={`watch-card-${w.slug}`}>
      <div className="relative">
        <WatchImage image={w.image} alt={`${w.brand} ${w.model}`} />
        <div className="absolute top-4 left-4"><AvailBadge value={w.availability} /></div>
        {w.is_sample && <span className="absolute top-4 right-4 text-[9px] tracking-[0.25em] uppercase text-[#A39E93] bg-[#0C0B09]/70 px-2 py-1">{t.sample}</span>}
      </div>
      <div className="pt-5 space-y-1.5">
        <p className="eyebrow">{w.brand}</p>
        <h3 className="text-xl text-[#F3EFE6] group-hover:text-[#E6D5AC] transition-colors duration-500">{w.model}</h3>
        <p className="text-xs text-[#A39E93] tracking-wider">{w.reference}</p>
        <div className="pt-2"><Price w={w} className="text-lg" /></div>
      </div>
    </Link>
    <button onClick={() => setOpen(true)} className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C8AE6E] hover:text-[#E6D5AC] transition-colors duration-300" data-testid={`watch-card-quick-${w.slug}`}>
      <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.4} /> {w.availability === "reserved" ? t.waitlist : w.availability === "on_order" ? t.order_wa : t.buy_wa}
    </button>
    {open && <QuickRequest w={w} open={open} onOpenChange={setOpen} />}
    </div>
  );
}
